import React, {useEffect, useRef, useState} from 'react';
import {
  Button,
  Circle,
  Flex,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Spinner,
  Text,
} from '@chakra-ui/react';
import {Checkmark} from '@carbon/icons-react';
import {useCheckWebIntentScriptDetection} from '../../hooks/api/integrations';

const pollIntervalMs = 5_000;

export function VerifyWebIntentInstallationModal({
  isOpen,
  onClose,
}: {
  isOpen: boolean;
  onClose: () => void;
}) {
  const [isDetected, setIsDetected] = useState(false);
  const checkDetection = useCheckWebIntentScriptDetection();
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const clearPolling = () => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  };

  const _checkDetection = () => {
    checkDetection.mutate(
      {},
      {
        onSuccess: ({detectedAt}) => {
          if (detectedAt) {
            setIsDetected(true);
            clearPolling();
          }
        },
      }
    );
  };

  useEffect(() => {
    if (!isOpen) {
      setIsDetected(false);
      clearPolling();
      return;
    }

    // Initial check
    _checkDetection();

    intervalRef.current = setInterval(_checkDetection, pollIntervalMs);

    return () => clearPolling();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  return (
    <Modal isCentered isOpen={isOpen} onClose={onClose} size="md">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Verify Installation</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <Flex direction="column" gap={4} align="center" py={4}>
            {isDetected ? (
              <>
                <Circle size={12} bg="kgreen.400" color="white">
                  <Checkmark size={24} />
                </Circle>
                <Text fontSize="lg" fontWeight="medium" textAlign="center">
                  Script detected!
                </Text>
                <Text color="kgray.300" textAlign="center">
                  Your tracking script is installed and working correctly.
                </Text>
              </>
            ) : (
              <>
                <Spinner size="xl" color="kblue.400" thickness="4px" />
                <Text fontSize="lg" fontWeight="medium" textAlign="center">
                  Waiting for script detection...
                </Text>
                <Text color="kgray.300" textAlign="center">
                  Open your website in a new tab and navigate to a page where
                  the tracking script is installed. We&apos;ll detect it
                  automatically.
                </Text>
              </>
            )}
          </Flex>
        </ModalBody>
        <ModalFooter>
          <Button
            colorScheme={isDetected ? 'green' : undefined}
            onClick={onClose}
          >
            {isDetected ? 'Done' : 'Close'}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
